import React from 'react';
import { useLocalStorage, useSessionStorage } from 'react-use';
import { Redirect, Route, Switch, useParams } from 'wouter';
import { Host } from '../../Host';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { EditorPage } from '../canvas/EditorPage';
import { KioskPage } from '../canvas/kiosk/KioskPage';
import { RecentBoardsView } from '../dashboard/boards/RecentBoardsView';
import { TeamBoardsView } from '../dashboard/boards/TeamBoardsView';
import { MembersView } from '../dashboard/members/MembersView';
import { TasksView } from '../dashboard/tasks/TasksView';
import { TemplatePreviewPage } from '../dashboard/templates/TemplatePreviewPage';
import { TemplatesView } from '../dashboard/templates/TemplatesView';
import { DemoPage } from '../dev/DemoPage';
import { DevPage } from '../dev/DevPage';
import { HatDevPage } from '../dev/HatDevPage';
import { LegacyBoardsPage } from '../legacy/LegacyBoardsPage';
import { NewManagementPage } from '../management/new/NewManagementPage';
import { TutorialsPage } from '../onboarding/TutorialsPage';
import { BasicTutorialPage } from '../onboarding/tutorials/TutorialPage';
import { TemplateLibraryPage } from '../templateLibrary/TemplateLibraryPage';
import { CreatePrivateWorkspacePage } from '../workspace/CreatePrivateWorkspacePage';
import { NewWorkspacePage } from '../workspace/NewWorkspacePage';
import { WorkspaceDragContext } from '../workspace/WorkspaceDragContext';
import { AccountModel } from './AccountModel';
import { OhNoPage } from './OhNoPage';

const RedirectKeepingQuery = React.memo(({ to }: { to: string }) => {
    const qs = new URLSearchParams(window.location.search).toString();
    const url = [to, qs].filter(x => x).join('?');
    return <Redirect to={url} />;
});

const RedirectToCheckout = () => {
    const { page } = useParams<{ page: string }>();
    window.location.href = `${Host.baseUrl}/checkout/${page}`;
    return null;
};

const WorkspaceRoutes = ({ entityId }: { entityId: string }) => <>
    <WorkspaceDragContext>
        <NewWorkspacePage entityId={entityId}>
            <Route path="/home">
                <RedirectKeepingQuery to="/boards" />
            </Route>
            <Route path="/boards/:folder?">
                <TeamBoardsView />
            </Route>
            <Route path="/recent">
                <UseLocationScope name="view" value="recent" label="Recent Boards" />
                <RecentBoardsView />
            </Route>
            <Route path="/tasks">
                <UseLocationScope name="view" value="tasks" label="Tasks" />
                <TasksView />
            </Route>
            <Route path="/members">
                <UseLocationScope name="view" value="members" label="Members" />
                <MembersView />
            </Route>
            <Route path="/templates/:primary?/:secondary?">
                <UseLocationScope name="view" value="templates" label="Templates" />
                <TemplatesView />
            </Route>
            <Route path="/">
                <Redirect to="/home" />
            </Route>
        </NewWorkspacePage>
    </WorkspaceDragContext>
</>;

const DefaultRoute = () => {
    const { id: accountId, workspaces } = useResolve(AccountModel);
    const [active] = useLocalStorage('deqo/ui/workspace', null);
    const [activeTemp] = useSessionStorage('deqo/ui/workspace');
    const defaultWorkspace = (
        workspaces.find(x => x.id === activeTemp) ??
        workspaces.find(x => x.id === active) ??
        workspaces.find(x => x.type === 'team')
    );
    if (defaultWorkspace) {
        // If user only has one workspace, go to the /boards for this workspace
        if (workspaces.length === 1) {
            return <RedirectKeepingQuery to={`~/${defaultWorkspace.id}/boards`} />;
        }
        else {
            return <RedirectKeepingQuery to={`~/${defaultWorkspace.id}/recent`} />;
        }
    };
    return <Redirect to={`~/${accountId}/recent`} />;
};

const EntityRoute = () => {
    const { entityId } = useParams<{ entityId: string, param?: string }>();
    if (entityId.match(/^BO\w{10}$/)) return <EditorPage />;
    if (entityId.match(/^WO\w{10}$/)) return <WorkspaceRoutes entityId={entityId} />;
    if (entityId.match(/^AC\w{10}$/)) return <WorkspaceRoutes entityId={entityId} />;
    if (entityId.match(/^TP\w+$/)) return <TemplatePreviewPage />;
    return <DefaultRoute />;
};

export const NewAccountSwitch = () =>
    <Switch>
        <Route path="/boards">
            <DefaultRoute />
        </Route>
        <Route path="/board/:entityId">
            <Redirect to="/:entityId" />
        </Route>
        <Route path="/checkout/:page?">
            <RedirectToCheckout />
        </Route>
        <Route path="/library*">
            <Redirect to="/templates" />
        </Route>
        <Route path="/live">
            <Redirect to="/recent" />
        </Route>
        <Route path="/manage/security/access">
            <Redirect to="/manage/access" />
        </Route>
        <Route path="/manage/security/sso">
            <Redirect to="/manage/sso" />
        </Route>
        <Route path="/demo" component={DemoPage} />
        <Route path="/dev/hats" component={HatDevPage} />
        <Route path="/dev" component={DevPage} />
        <Route path="/kiosk" component={KioskPage} />
        <Route path="/legacy" component={LegacyBoardsPage} />
        <Route path="/manage/:section?/:param?" component={NewManagementPage} />
        <Route path="/ohno" component={OhNoPage} />
        <Route path="/private" component={CreatePrivateWorkspacePage} />
        <Route path="/templates/:group?" component={TemplateLibraryPage} />
        <Route path="/tp/:entityId" nest component={TemplatePreviewPage} />
        <Route path="/TP/:entityId" nest component={TemplatePreviewPage} />
        <Route path="/tutorials-test" component={BasicTutorialPage} />
        <Route path="/tutorials" component={TutorialsPage} />
        {/* These have to go last */}
        <Route path="/:entityId" nest component={EntityRoute} />
        <Route path="*" component={DefaultRoute} />
    </Switch>
    ;
